import { ExpertNoteIdentifier, FieldProvenanceDto, MissionAssessmentFieldName } from '@@types/api.types';
import { FieldProvenanceBadge } from '@components/shared/field-provenance-badge.component';
import { FormLabel } from '@components/shared/form-label.component';
import { FormSuggestion, useFormSuggestion } from '@components/shared/form-suggestion.component';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@components/ui/accordion';
import { withForm } from '@components/ui/form/form';
import { config } from '@config';
import maDefinitions from '@data/ma-definitions.json';
import { useScrollToField } from '@hooks/use-scroll-to-field.hook';
import { missionAssessmentExpertNoteBaseSchema } from '@schemas/mission-assessment.schema';
import { PHASES_OF_OPERATIONS_LABELS } from '@utils/constants';
import { createSuggestionTextApplier } from '@utils/form.util';
import type { ProvenanceAcceptEntry } from '@utils/mission-assessment-provenance.util';
import type { ReactNode } from 'react';
import { useRef } from 'react';
import { missionAssessmentDraftFormOptions } from './mission-assessment-form-options';
import { MissionAssessmentGenerateButton } from './mission-assessment-generate-button.component';

interface RangeOption {
  value: string;
  label: string;
}

interface ExpertNoteConfig {
  identifier: ExpertNoteIdentifier;
  title: string;
  guidance: ReactNode;
}

export const expertNotesConfig: ExpertNoteConfig[] = [
  {
    identifier: ExpertNoteIdentifier.THREATS,
    title: 'Threats and Responses',
    guidance: (
      <div className="text-xs text-gray-600 space-y-3">
        <p>
          Examine the technology's defensive and offensive capabilities against current and projected threat
          environments. Evaluate how the technology addresses identified vulnerabilities while considering adversary
          countermeasures and evolutionary threat trajectories. Document specific threat scenarios where the technology
          provides advantage, noting survivability characteristics in contested operational environments.
        </p>
        <div className="space-y-1">
          <p className="font-medium">Key Areas:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Specify threat categories: kinetic, cyber, electronic warfare, hybrid</li>
            <li>Identify technology's defensive mechanisms and offensive capabilities</li>
            <li>Document survivability metrics in contested environments</li>
            <li>Note threat evolution timelines versus technology lifecycle</li>
          </ul>
        </div>
        <p className="text-xs p-2 rounded bg-[#3D2EFF1A] text-[#3D2EFF]">
          <span className="font-medium">DOTMLPF-P Considerations:</span> Policy constraints may limit threat response
          options, while Doctrine determines engagement protocols.
        </p>
      </div>
    ),
  },
  {
    identifier: ExpertNoteIdentifier.STRATEGIC,
    title: 'Strategic and Operational Alignment',
    guidance: (
      <div className="text-xs text-gray-600 space-y-3">
        <p>
          Assess technology integration within existing operational concepts and strategic objectives. Evaluate
          interoperability requirements across joint operations, coalition partnerships, and mission command structures.
          Determine how the technology enhances operational tempo while supporting mission threads and decision-making
          processes.
        </p>
        <div className="space-y-1">
          <p className="font-medium">Key Points:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Map technology to specific strategic objectives and operational concepts</li>
            <li>Identify interoperability gaps with existing systems</li>
            <li>Document mission thread contributions and command integration</li>
            <li>Specify joint operation requirements and coalition compatibility</li>
          </ul>
        </div>
        <p className="text-xs p-2 rounded bg-[#3D2EFF1A] text-[#3D2EFF]">
          <span className="font-medium">DOTMLPF-P Integration:</span> Organization structures require modification for
          technology integration, while Training programs must accommodate new operational procedures. Leadership and
          Education components ensure proper strategic implementation.
        </p>
      </div>
    ),
  },
  {
    identifier: ExpertNoteIdentifier.RISKS,
    title: 'Risks and Issues',
    guidance: (
      <div className="text-xs text-gray-600 space-y-3">
        <p>
          Conduct comprehensive risk assessment using Technology Readiness Level methodologies and technical maturity
          indicators. Identify critical technology elements requiring further development, assess integration
          challenges, and evaluate programmatic risks including cost, schedule, and performance uncertainties.
        </p>
        <div className="space-y-1">
          <p className="font-medium">Assessment Areas:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Document Technology Readiness Levels for critical components</li>
            <li>Identify technical, programmatic, and operational risk categories</li>
            <li>Specify integration challenges with existing systems</li>
            <li>Quantify cost, schedule, and performance risk probabilities</li>
          </ul>
        </div>
        <p className="text-xs p-2 rounded bg-[#3D2EFF1A] text-[#3D2EFF]">
          <span className="font-medium">DOTMLPF-P Risk Analysis:</span> Personnel availability affects implementation
          timelines, while Facilities requirements may create infrastructure risks. Materiel dependencies introduce
          supply chain vulnerabilities.
        </p>
      </div>
    ),
  },
  {
    identifier: ExpertNoteIdentifier.SUSTAINMENT,
    title: 'Sustainment Considerations',
    guidance: (
      <div className="text-xs text-gray-600 space-y-3">
        <p>
          Evaluate lifecycle support requirements including maintenance concepts, supply chain sustainability, and
          technology refresh needs. Assess reliability, availability, and maintainability characteristics while
          considering depot-level maintenance capabilities and industrial base capacity.
        </p>
        <div className="space-y-1">
          <p className="font-medium">Evaluation Criteria:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Document maintenance concept and support infrastructure requirements</li>
            <li>Identify supply chain dependencies and industrial base capacity</li>
            <li>Specify technology refresh cycles and obsolescence management</li>
            <li>Quantify lifecycle costs and sustainment resource requirements</li>
          </ul>
        </div>
        <p className="text-xs p-2 rounded bg-[#3D2EFF1A] text-[#3D2EFF]">
          <span className="font-medium">DOTMLPF-P Sustainment Framework:</span> Facilities must support maintenance
          operations, Personnel require specialized training for sustainment activities, and Policy governs logistics
          and support procedures.
        </p>
      </div>
    ),
  },
];

const phaseOptions = Array.from({ length: PHASES_OF_OPERATIONS_LABELS.length }, (_, i) => ({
  value: i.toString(),
  label: `${i} - ${PHASES_OF_OPERATIONS_LABELS[i]}`,
}));

const generatePhaseOptions = (
  selectedMin?: number | null,
  selectedMax?: number | null,
  isMaxOptions = false,
): RangeOption[] => {
  return phaseOptions.filter((option) => {
    const value = parseInt(option.value, 10);
    if (isMaxOptions && selectedMin !== undefined && selectedMin !== null && value < selectedMin) {
      return false;
    }
    if (!isMaxOptions && selectedMax !== undefined && selectedMax !== null && value > selectedMax) {
      return false;
    }
    return true;
  });
};

const isAIEnabled = config.ai.enabled;

export const MissionAssessmentFormNotes = withForm({
  ...missionAssessmentDraftFormOptions,
  props: {
    onProvenanceAccept: undefined as ((entries: ProvenanceAcceptEntry[]) => void) | undefined,
    provenance: undefined as Record<string, FieldProvenanceDto> | null | undefined,
    technologyId: undefined as string | undefined,
  },
  render: function Render({ form, onProvenanceAccept, provenance, technologyId }) {
    // Create refs for fields that have suggestions
    const threatsRef = useRef<HTMLDivElement>(null);
    const strategicRef = useRef<HTMLDivElement>(null);
    const risksRef = useRef<HTMLDivElement>(null);
    const sustainmentRef = useRef<HTMLDivElement>(null);
    const generalNotesRef = useRef<HTMLLabelElement>(null);

    // Set up scroll to field functionality
    const scrollToField = useScrollToField<MissionAssessmentFieldName>({
      [MissionAssessmentFieldName.THREATS]: threatsRef,
      [MissionAssessmentFieldName.STRATEGIC]: strategicRef,
      [MissionAssessmentFieldName.RISKS]: risksRef,
      [MissionAssessmentFieldName.SUSTAINMENT]: sustainmentRef,
      [MissionAssessmentFieldName.GENERAL_NOTES]: generalNotesRef,
    });

    const {
      handleSuggestionStream,
      acceptSuggestion,
      rejectSuggestion,
      getSuggestion,
      getSuggestionModel,
      getIsStreaming,
      handleMetadata,
      handleStreamingStart,
      handleStreamingEnd,
    } = useFormSuggestion<MissionAssessmentFieldName>(scrollToField);

    const writeExpertNote = (identifier: ExpertNoteIdentifier) => (text: string) => {
      const noteIndex = expertNotesConfig.findIndex((noteConfig) => noteConfig.identifier === identifier);
      if (noteIndex !== -1) {
        form.setFieldValue(`data.expertNotes[${noteIndex}].content`, text);
      }
    };

    const applySuggestionText = createSuggestionTextApplier<MissionAssessmentFieldName>({
      [MissionAssessmentFieldName.GENERAL_NOTES]: (text) => form.setFieldValue('data.generalNotes', text),
      [MissionAssessmentFieldName.RISKS]: writeExpertNote(ExpertNoteIdentifier.RISKS),
      [MissionAssessmentFieldName.STRATEGIC]: writeExpertNote(ExpertNoteIdentifier.STRATEGIC),
      [MissionAssessmentFieldName.SUSTAINMENT]: writeExpertNote(ExpertNoteIdentifier.SUSTAINMENT),
      [MissionAssessmentFieldName.THREATS]: writeExpertNote(ExpertNoteIdentifier.THREATS),
    });

    const handleAcceptSuggestion = (fieldName: MissionAssessmentFieldName) => {
      acceptSuggestion(fieldName, (text, meta) => {
        applySuggestionText(fieldName, typeof text === 'string' ? text : '');
        onProvenanceAccept?.([{ key: fieldName, llmLogId: meta?.llmLogId }]);
      });
    };
    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-lg text-gray-700 mb-4">Capability Assessment</h3>

          <div className="space-y-6">
            <form.AppField
              name="data.expertNotes"
              mode="array"
            >
              {(field) => (
                <div className="space-y-6">
                  {expertNotesConfig.map((config, index) => {
                    const getFieldNameFromIdentifier = (
                      identifier: ExpertNoteIdentifier,
                    ): MissionAssessmentFieldName => {
                      switch (identifier) {
                        case ExpertNoteIdentifier.THREATS:
                          return MissionAssessmentFieldName.THREATS;
                        case ExpertNoteIdentifier.STRATEGIC:
                          return MissionAssessmentFieldName.STRATEGIC;
                        case ExpertNoteIdentifier.RISKS:
                          return MissionAssessmentFieldName.RISKS;
                        case ExpertNoteIdentifier.SUSTAINMENT:
                          return MissionAssessmentFieldName.SUSTAINMENT;
                        default:
                          return MissionAssessmentFieldName.THREATS;
                      }
                    };
                    const fieldName = getFieldNameFromIdentifier(config.identifier);
                    const getSectionRef = (identifier: ExpertNoteIdentifier) => {
                      switch (identifier) {
                        case ExpertNoteIdentifier.THREATS:
                          return threatsRef;
                        case ExpertNoteIdentifier.STRATEGIC:
                          return strategicRef;
                        case ExpertNoteIdentifier.RISKS:
                          return risksRef;
                        case ExpertNoteIdentifier.SUSTAINMENT:
                          return sustainmentRef;
                        default:
                          return threatsRef;
                      }
                    };
                    const sectionRef = getSectionRef(config.identifier);
                    return (
                      <div
                        key={config.identifier}
                        className="space-y-2"
                        ref={sectionRef}
                      >
                        <Accordion
                          type="single"
                          collapsible
                          className="rounded-lg"
                        >
                          <AccordionItem
                            value="instructions"
                            className="border-none"
                          >
                            <div className="flex justify-between">
                              <div className="flex items-center gap-1.5">
                                <FormLabel label={config.title} />
                                <FieldProvenanceBadge
                                  context="form"
                                  provenance={provenance?.[fieldName]}
                                />
                              </div>

                              <AccordionTrigger className="p-0 border-none text-sm font-normal flex items-center text-gray-700 hover:no-underline">
                                <p className="ml-auto pr-2">Instructions</p>
                              </AccordionTrigger>
                            </div>

                            <AccordionContent className="px-4 pb-4 bg-gray-100 rounded-lg pt-4 mt-2">
                              {config.guidance}
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>

                        <form.AppField
                          name={`data.expertNotes[${index}].content`}
                          validators={{
                            onBlur: missionAssessmentExpertNoteBaseSchema.shape.content,
                          }}
                        >
                          {(subField) => (
                            <div className="space-y-2">
                              <subField.TextAreaField
                                generateButton={
                                  technologyId &&
                                  isAIEnabled && (
                                    <MissionAssessmentGenerateButton
                                      fieldName={fieldName}
                                      onAccept={(text) => applySuggestionText(fieldName, text)}
                                      onStream={(text) => handleSuggestionStream({ key: fieldName, text })}
                                      onMetadata={(metadata) => handleMetadata({ key: fieldName, metadata })}
                                      onStreamingStart={() => handleStreamingStart(fieldName)}
                                      onStreamingEnd={() => handleStreamingEnd(fieldName)}
                                      technologyId={technologyId}
                                    />
                                  )
                                }
                                rows={4}
                                placeholder={`${config.title} Notes`}
                              />

                              <FormSuggestion
                                suggestion={getSuggestion(fieldName)}
                                onAccept={() => handleAcceptSuggestion(fieldName)}
                                onReject={() => rejectSuggestion(fieldName)}
                                rejectButtonProps={{ variant: 'destructive' }}
                                model={getSuggestionModel(fieldName)}
                                isStreaming={getIsStreaming(fieldName)}
                              />
                            </div>
                          )}
                        </form.AppField>

                        <form.AppField
                          name={`data.expertNotes[${index}].identifier`}
                          defaultValue={config.identifier}
                        >
                          {(subField) => (
                            <input
                              type="hidden"
                              name={subField.name}
                              value={subField.state.value || config.identifier}
                            />
                          )}
                        </form.AppField>
                      </div>
                    );
                  })}
                </div>
              )}
            </form.AppField>

            <form.AppField name="data.generalNotes">
              {(field) => (
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5">
                    <FormLabel
                      label="Assessment General Notes"
                      htmlFor={field.name}
                    />
                    <FieldProvenanceBadge
                      context="form"
                      provenance={provenance?.[MissionAssessmentFieldName.GENERAL_NOTES]}
                    />
                  </div>

                  <field.TextAreaField
                    placeholder="Assessment General Notes"
                    rows={4}
                    generateButton={
                      technologyId &&
                      isAIEnabled && (
                        <MissionAssessmentGenerateButton
                          fieldName={MissionAssessmentFieldName.GENERAL_NOTES}
                          onAccept={(text) => applySuggestionText(MissionAssessmentFieldName.GENERAL_NOTES, text)}
                          onMetadata={(metadata) =>
                            handleMetadata({ key: MissionAssessmentFieldName.GENERAL_NOTES, metadata })
                          }
                          onStream={(text) =>
                            handleSuggestionStream({ key: MissionAssessmentFieldName.GENERAL_NOTES, text })
                          }
                          onStreamingStart={() => handleStreamingStart(MissionAssessmentFieldName.GENERAL_NOTES)}
                          onStreamingEnd={() => handleStreamingEnd(MissionAssessmentFieldName.GENERAL_NOTES)}
                          technologyId={technologyId}
                        />
                      )
                    }
                  />

                  <FormSuggestion
                    model={getSuggestionModel(MissionAssessmentFieldName.GENERAL_NOTES)}
                    onAccept={() => handleAcceptSuggestion(MissionAssessmentFieldName.GENERAL_NOTES)}
                    onReject={() => rejectSuggestion(MissionAssessmentFieldName.GENERAL_NOTES)}
                    rejectButtonProps={{ variant: 'destructive' }}
                    suggestion={getSuggestion(MissionAssessmentFieldName.GENERAL_NOTES)}
                    isStreaming={getIsStreaming(MissionAssessmentFieldName.GENERAL_NOTES)}
                  />
                </div>
              )}
            </form.AppField>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1">
          <div className="col-span-1 md:col-span-2">
            <FormLabel
              label="Phases of Operation"
              tooltip={maDefinitions?.phaseOfOperations}
            />
          </div>

          <form.Subscribe selector={(state) => state.values.data}>
            {(data) => {
              const minPhase = data?.minPhase;
              const maxPhase = data?.maxPhase;

              return (
                <>
                  <form.AppField
                    name="data.minPhase"
                    validators={{
                      onChangeListenTo: ['data.maxPhase'],
                    }}
                  >
                    {(field) => {
                      return (
                        <field.SelectField
                          clearable
                          options={generatePhaseOptions(undefined, maxPhase)}
                          placeholder="Select Min Phase"
                        />
                      );
                    }}
                  </form.AppField>

                  <form.AppField
                    name="data.maxPhase"
                    validators={{
                      onChangeListenTo: ['data.minPhase'],
                    }}
                  >
                    {(field) => {
                      return (
                        <field.SelectField
                          clearable
                          options={generatePhaseOptions(minPhase, undefined, true)}
                          placeholder="Select Max Phase"
                        />
                      );
                    }}
                  </form.AppField>
                </>
              );
            }}
          </form.Subscribe>
        </div>
      </div>
    );
  },
});
